import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Loader2, CheckCircle2, CreditCard, Facebook } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTION_TYPES = [
  "Broiler chicken production",
  "Turkey production",
  "Egg / layer production",
  "Poultry barn owner / operator",
  "Other agricultural operation",
];

const SERVICE_OPTIONS = [
  "Industrial Manure Removal",
  "Professional Barn Cleaning",
  "Agricultural Sanitation",
  "Barn Maintenance",
  "Flock Turnaround Prep",
  "Custom Cleaning Program",
];

const inputCls =
  "w-full rounded-lg bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none focus:border-transparent transition duration-200";

const emptyForm = {
  name: "",
  farm_name: "",
  email: "",
  phone: "",
  production_type: PRODUCTION_TYPES[0],
  services: [],
  barn_count: "",
  barn_size: "",
  flock_size: "",
  location: "",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const [showOwnerPay, setShowOwnerPay] = useState(false);
  const [payCode, setPayCode] = useState(() => localStorage.getItem("imr_admin_code") || "");
  const [payAmount, setPayAmount] = useState("");
  const [payEmail, setPayEmail] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [payLink, setPayLink] = useState("");

  const createPaymentLink = async (e) => {
    e.preventDefault();
    setPayBusy(true);
    try {
      const res = await axios.post(
        `${API}/payments/custom-checkout`,
        {
          amount: Number(payAmount),
          customer_email: payEmail || null,
          origin_url: window.location.origin,
        },
        { headers: { "x-admin-code": payCode } }
      );
      localStorage.setItem("imr_admin_code", payCode);
      setPayLink(res.data.checkout_url);
      toast.success("Payment link created — copy and send it to your customer.");
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) toast.error("Wrong admin code.");
      else if (status === 503) toast.error("Payments aren't live on this environment yet — deploy to activate.");
      else toast.error("Could not create the payment link.");
    } finally {
      setPayBusy(false);
    }
  };

  const [photos, setPhotos] = useState([]);

  const toggleService = (service) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(service)
        ? f.services.filter((s) => s !== service)
        : [...f.services, service],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/enquiries`, {
        ...form,
        barn_count: form.barn_count ? Number(form.barn_count) : null,
        flock_size: form.flock_size ? Number(form.flock_size) : null,
      });
      if (photos.length > 0) {
        try {
          const data = new FormData();
          photos.forEach((p) => data.append("files", p));
          await axios.post(`${API}/enquiries/${res.data.id}/photos`, data);
        } catch (photoErr) {
          toast.warning("Enquiry sent, but the photo didn't upload — you can email it to imrpoultrysolutions@gmail.com");
        }
      }
      setSent(true);
      toast.success("Enquiry sent — the IMR team will be in touch shortly.");
      setForm(emptyForm);
      setPhotos([]);
    } catch (err) {
      toast.error("Could not send your enquiry. Please call us at 1-970-902-3321.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="grain-overlay bg-[#081C15] text-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl mb-14">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37] mb-4">Contact Us</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight" data-testid="contact-heading">
            Let's talk about your barns
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#D8E2DC] leading-relaxed">
            Farm owners, poultry producers, and agricultural businesses — request information
            or discuss your service requirements with the IMR team.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="space-y-5"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Your Name *</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={update("name")} placeholder="Jane Doe" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-farm" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Farm / Company</label>
                <input id="contact-farm" data-testid="contact-farm-input" value={form.farm_name} onChange={update("farm_name")} placeholder="Valley Poultry Farms" className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-email" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Email *</label>
                <input id="contact-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={update("email")} placeholder="you@farm.ca" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Phone</label>
                <input id="contact-phone" type="tel" data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} placeholder="250-555-0100" className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-type" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Operation Type</label>
                <select id="contact-type" data-testid="contact-type-select" value={form.production_type} onChange={update("production_type")} className={`${inputCls} appearance-none`}>
                  {PRODUCTION_TYPES.map((t) => (
                    <option key={t} value={t} className="text-[#081C15]">{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-location" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Farm Location (B.C.)</label>
                <input id="contact-location" data-testid="contact-location-input" value={form.location} onChange={update("location")} placeholder="e.g. Chilliwack, BC" className={inputCls} />
              </div>
            </div>
            <div>
              <span className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Services You Need</span>
              <div className="flex flex-wrap gap-2.5" data-testid="contact-services-group">
                {SERVICE_OPTIONS.map((s) => {
                  const active = form.services.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      data-testid={`contact-service-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => toggleService(s)}
                      className={`rounded-full px-4 py-2 text-xs font-bold transition-colors duration-200 ${
                        active
                          ? "bg-[#D4AF37] text-[#081C15]"
                          : "bg-white/5 border border-white/15 text-[#D8E2DC] hover:border-[#D4AF37]"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-barn-count" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Number of Barns</label>
                <input id="contact-barn-count" type="number" min="1" data-testid="contact-barn-count-input" value={form.barn_count} onChange={update("barn_count")} placeholder="e.g. 3" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-barn-size" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Barn Size</label>
                <input id="contact-barn-size" data-testid="contact-barn-size-input" value={form.barn_size} onChange={update("barn_size")} placeholder="e.g. 40 × 400 ft or 16,000 sq ft" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="contact-flock-size" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Number of Birds (max 60,000)</label>
              <input id="contact-flock-size" type="number" min="1" max="60000" data-testid="contact-flock-size-input" value={form.flock_size} onChange={update("flock_size")} placeholder="e.g. 25,000" className={inputCls} />
            </div>
            <div>
              <label htmlFor="contact-photos" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Attach Barn Photos (optional, up to 5)</label>
              <input
                id="contact-photos"
                type="file"
                accept="image/*"
                multiple
                data-testid="contact-photos-input"
                onChange={(e) => setPhotos(Array.from(e.target.files).slice(0, 5))}
                className="text-sm text-[#D8E2DC] file:mr-3 file:rounded-full file:border-0 file:bg-[#1B4332] file:text-white file:px-4 file:py-2 file:text-xs file:font-bold hover:file:bg-[#081C15] file:transition-colors"
              />
              {photos.length > 0 && (
                <p className="mt-2 text-xs text-[#D8E2DC]/70" data-testid="contact-photos-count">{photos.length} photo{photos.length > 1 ? "s" : ""} attached</p>
              )}
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC] mb-2">Tell us about your requirements *</label>
              <textarea id="contact-message" data-testid="contact-message-input" required rows={4} value={form.message} onChange={update("message")} placeholder="Number of barns, flock cycle timing, cleaning requirements..." className={inputCls} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-[#D4AF37] text-[#081C15] px-8 py-3.5 font-display font-bold text-sm hover:brightness-110 disabled:opacity-60 transition duration-200 shadow-[0_4px_24px_rgba(212,175,55,0.35)]"
            >
              {sending ? <Loader2 size={17} className="animate-spin" /> : sent ? <CheckCircle2 size={17} /> : <Send size={17} />}
              {sending ? "Sending..." : "Send Enquiry"}
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="space-y-5"
          >
            <a
              href="tel:+19709023321"
              data-testid="contact-call-card"
              className="flex items-center gap-5 rounded-xl bg-white/5 border border-white/15 p-6 hover:border-[#D4AF37] transition-colors duration-200"
            >
              <span className="w-12 h-12 rounded-lg bg-[#D4AF37] flex items-center justify-center shrink-0">
                <Phone size={21} className="text-[#081C15]" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC]">Call us directly</p>
                <p className="font-display font-bold text-xl mt-1">1-970-902-3321 <span className="text-sm font-semibold text-[#D8E2DC]">— Iancu Alin</span></p>
                <p className="font-display font-bold text-lg mt-1">+1 (236) 777-0697 <span className="text-sm font-semibold text-[#D8E2DC]">— Matt Toews</span></p>
              </div>
            </a>

            <a
              href="mailto:imrpoultrysolutions@gmail.com"
              data-testid="contact-email-card"
              className="flex items-center gap-5 rounded-xl bg-white/5 border border-white/15 p-6 hover:border-[#D4AF37] transition-colors duration-200"
            >
              <span className="w-12 h-12 rounded-lg bg-[#D4AF37] flex items-center justify-center shrink-0">
                <Mail size={21} className="text-[#081C15]" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC]">Email us</p>
                <p className="font-display font-bold text-xl mt-1 break-all">imrpoultrysolutions@gmail.com</p>
              </div>
            </a>

            <a
              href="https://www.facebook.com/yanku.teberu.3/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook-card"
              className="flex items-center gap-5 rounded-xl bg-white/5 border border-white/15 p-6 hover:border-[#D4AF37] transition-colors duration-200"
            >
              <span className="w-12 h-12 rounded-lg bg-[#D4AF37] flex items-center justify-center shrink-0">
                <Facebook size={21} className="text-[#081C15]" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC]">Follow us</p>
                <p className="font-display font-bold text-xl mt-1">Facebook</p>
              </div>
            </a>

            <div className="rounded-xl bg-[#1B4332] border border-[#1B4332] p-6" data-testid="contact-service-area">
              <div className="flex items-start gap-5">
                <span className="w-12 h-12 rounded-lg bg-[#D4AF37] flex items-center justify-center shrink-0">
                  <MapPin size={21} className="text-[#081C15]" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC]">Our Office</p>
                  <p className="font-display font-bold text-xl mt-1">2468 Waverly Dr</p>
                  <p className="text-sm text-[#D8E2DC] mt-0.5">Blind Bay, BC V0E 1H2</p>
                  <p className="mt-1.5 text-xs text-[#D8E2DC]/80 italic">By appointment only — please call or email before visiting.</p>
                  <p className="mt-3 text-xs font-bold uppercase tracking-[0.15em] text-[#D8E2DC]">Service Area</p>
                  <p className="font-display font-bold text-base mt-1">Across British Columbia, Canada</p>
                  <p className="mt-2 text-sm text-[#D8E2DC] leading-relaxed">
                    We work across British Columbia. Anything outside a 100 km radius of
                    Vancouver is charged per kilometre — ask us for a travel-inclusive quote.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-[#D4AF37] text-[#081C15] p-6" data-testid="deposit-card">
              <div className="flex items-start gap-5">
                <span className="w-12 h-12 rounded-lg bg-[#081C15] flex items-center justify-center shrink-0">
                  <CreditCard size={21} className="text-[#D4AF37]" />
                </span>
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#081C15]/70">Ready to book?</p>
                  <p className="font-display font-bold text-xl mt-1">Secure your spot with a deposit</p>
                  <p className="mt-1.5 text-sm text-[#081C15]/80 leading-relaxed">
                    We require half payment down to secure your booking — the remaining balance
                    is due upon completion. Call or email us to get your quote and booking started.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <a
                      href="tel:+19709023321"
                      data-testid="deposit-call-button"
                      className="inline-flex items-center gap-2 rounded-full bg-[#081C15] text-white px-6 py-3 font-display font-bold text-sm hover:bg-[#1B4332] transition-colors duration-200"
                    >
                      <Phone size={16} />
                      Call to Book
                    </a>
                    <button
                      type="button"
                      onClick={() => setShowOwnerPay(!showOwnerPay)}
                      data-testid="owner-pay-toggle"
                      className="inline-flex items-center gap-2 rounded-full border-2 border-[#081C15] text-[#081C15] px-6 py-3 font-display font-bold text-sm hover:bg-[#081C15] hover:text-white transition-colors duration-200"
                    >
                      <CreditCard size={16} />
                      {showOwnerPay ? "Close Payment Link Tool" : "Create Payment Link (IMR Team)"}
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-[#081C15]/60" data-testid="deposit-terms">
                    Terms: 50% down payment required to book. Remaining balance due upon completion.
                  </p>

                  {showOwnerPay && (
                    <form onSubmit={createPaymentLink} className="mt-3 rounded-lg bg-white/60 border border-[#081C15]/15 p-4 space-y-3" data-testid="owner-pay-form">
                      <div className="grid sm:grid-cols-3 gap-3">
                        <input
                          type="password"
                          required
                          data-testid="owner-pay-code"
                          value={payCode}
                          onChange={(e) => setPayCode(e.target.value)}
                          placeholder="Owner code"
                          className="rounded-lg bg-white border border-[#081C15]/15 px-3 py-2.5 text-sm text-[#081C15] placeholder:text-gray-400 focus:ring-2 focus:ring-[#1B4332] focus:outline-none"
                        />
                        <input
                          type="number"
                          required
                          min="1"
                          step="0.01"
                          data-testid="owner-pay-amount"
                          value={payAmount}
                          onChange={(e) => setPayAmount(e.target.value)}
                          placeholder="Amount (CAD)"
                          className="rounded-lg bg-white border border-[#081C15]/15 px-3 py-2.5 text-sm text-[#081C15] placeholder:text-gray-400 focus:ring-2 focus:ring-[#1B4332] focus:outline-none"
                        />
                        <input
                          type="email"
                          data-testid="owner-pay-email"
                          value={payEmail}
                          onChange={(e) => setPayEmail(e.target.value)}
                          placeholder="Customer email (optional)"
                          className="rounded-lg bg-white border border-[#081C15]/15 px-3 py-2.5 text-sm text-[#081C15] placeholder:text-gray-400 focus:ring-2 focus:ring-[#1B4332] focus:outline-none"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={payBusy}
                        data-testid="owner-pay-create"
                        className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-white px-5 py-2.5 font-display font-bold text-xs hover:bg-[#081C15] disabled:opacity-60 transition-colors duration-200"
                      >
                        {payBusy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
                        {payBusy ? "Creating..." : "Create Payment Link"}
                      </button>
                      {payLink && (
                        <div className="rounded-lg bg-[#081C15] p-3 flex items-center gap-3" data-testid="owner-pay-result">
                          <p className="flex-1 text-xs text-white/80 break-all">{payLink}</p>
                          <button
                            type="button"
                            data-testid="owner-pay-copy"
                            onClick={() => { navigator.clipboard.writeText(payLink); toast.success("Link copied."); }}
                            className="shrink-0 rounded-full bg-[#D4AF37] text-[#081C15] px-4 py-2 font-display font-bold text-xs hover:brightness-110 transition duration-200"
                          >
                            Copy
                          </button>
                        </div>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </div>

            <p className="text-xs text-white/40 leading-relaxed" data-testid="contact-hours-note">
              Prefer email? Send your barn details to imrpoultrysolutions@gmail.com and the IMR team
              will follow up with service information.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
